import {Either, getEq, left, Left, Right, right} from 'fp-ts/lib/Either';
import {Eq, eqString, getStructEq} from 'fp-ts/lib/Eq';
import {ToMatchOptions} from '../../Shared/utilities/MapDiscriminatedUnion';
import {Async, getAsyncEq, Loaded} from './Async';

export class UnvalidatedEmail {
  public readonly key = 'UnvalidatedEmail';
  public readonly validationError = "The email can't be validated.";

  constructor(public readonly address: string) {}
}

export class InvalidEmail {
  public static create(email: string) {
    return email === '' ? new UnvalidatedEmail('') : new InvalidEmail(email);
  }
  public readonly key = 'InvalidEmail';
  public readonly validationError = 'The e-mail is invalid.';

  private constructor(public readonly address: string) {}
}

export const emailValidationRegex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export class ValidEmail {
  public static create(email: string) {
    // eslint-disable-next-line
    const emailRegularExpression = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return emailRegularExpression.test(email) ? new ValidEmail(email) : InvalidEmail.create(email);
  }

  public readonly key = 'ValidEmail';
  private constructor(public readonly address: string) {}
}

export const isValidEmail = (email: Email): email is ValidEmail => email.key === 'ValidEmail';

export type Email = ValidEmail | InvalidEmail | UnvalidatedEmail;

export type EmailOptions<TReturn> = ToMatchOptions<Email, 'key', TReturn>;

export const matchEmail = <TReturn>(email: Email, options: EmailOptions<TReturn>): TReturn => {
  switch (email.key) {
    case 'InvalidEmail':
      return options.InvalidEmail(email);
    case 'UnvalidatedEmail':
      return options.UnvalidatedEmail(email);
    case 'ValidEmail':
      return options.ValidEmail(email);
  }
};

export const eqEmail: Eq<Email> = getStructEq({
  address: eqString,
});

export type ValidatableEmail = Async<Either<Email, ValidEmail>>;

export const eqValidatableEmail: Eq<ValidatableEmail> = getAsyncEq(getEq(eqEmail, eqEmail));

export type ValidatedEmail = Loaded<Right<ValidEmail>>;

export const createValidatedEmail = (value: string): Loaded<Right<ValidEmail>> =>
  new Loaded(right(ValidEmail.create(value) as ValidEmail)) as Loaded<Right<ValidEmail>>;

export const createValidatableEmptyEmail = (): Loaded<Left<UnvalidatedEmail>> =>
  new Loaded(left(new UnvalidatedEmail(''))) as Loaded<Left<UnvalidatedEmail>>;

export const isEmptyEmail = (email: Email): email is UnvalidatedEmail => email.key === 'UnvalidatedEmail';